/* eslint-disable no-restricted-syntax */
import { Autocomplete, MenuItem, CircularProgress, Chip, IconButton, Icon } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import lod_ from "lodash";
import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import { replaceInTextHbr } from "helpers/utilities";
import { handleSpecialCell } from "components/Custom/Tables/DefaultDataTable/functions";
import { queryDictionary } from "helpers/dictionary";
import { useDispatch, useSelector } from "react-redux";
import FormAction from "redux-react/actions/formAction";
import { display } from "redux-react/reducers/snackBarReducer";
import { t } from "i18next";

/**
 * Whitelist dynamic input component V2
 * This component allows users to select items from a dynamic whitelist based on API actions.
 * It supports both single and multiple selections, loading suggestions from a specified API,
 * and rendering options in a customizable way.
 * @param {Object} props - The component props
 * @param {string} props.label - The label for the input field
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string|Array} props.value - The current value(s) of the input
 * @param {Function} props.onChange - Callback function to handle value changes
 * @param {Object} props.item - The item configuration containing whitelistDynamic and other properties
 * @param {Array} props.listOfApis - List of available APIs to fetch data from
 * @param {string} props.error - Error message to display if any
 * @param {Object} props.InputProps - Additional props for the input component
 * @param {boolean} props.isArray - Whether the input is expected to handle an array
 * @param {...Object} rest - Additional props to pass to the Autocomplete component
 * @returns {JSX.Element} - Rendered Autocomplete component with dynamic suggestions
 */
const WhitelistItemV2 = ({
	label,
	disabled,
	value,
	onChange,
	item,
	listOfApis,
	error,
	InputProps,
	isArray = false,
	...rest
}) => {
	const dispatch = useDispatch();
	const assistantID = useSelector(state => selectCurrentProfile(state).assistantID);
	const { user } = useSelector(state => state);

	const [dictionary, setDictionary] = useState({});
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSearchText, setCurrentSearchText] = useState("");
	const [inputValue, setInputValue] = useState("");

	const [currentValue, setCurrentValue] = useState(null);

	let whitelistDynamic = item.whitelistDynamic || {};

	function displayError(
		error = t("FORMS.ERRORS.cannotLoadAPIDatas", {
			label
		})
	) {
		dispatch(
			display({
				message: error,
				type: "error"
			})
		);
	}

	/**
	 * Get correct API and action
	 * @returns {Object} - Object containing the API and action if found, otherwise an empty object
	 */
	function getCorrectAction() {
		const apiName = whitelistDynamic?.apiName;
		const actionName = whitelistDynamic?.action;
		if (lod_.isNil(apiName) || lod_.isNil(actionName)) {
			return {};
		}
		const api = listOfApis.find(item => item.name === apiName);
		if (lod_.isNil(api) || lod_.isEmpty(api)) {
			return {};
		}
		const action = lod_.get(api, `config.action.${actionName}`, null);
		if (lod_.isNil(action) || lod_.isEmpty(action)) {
			return {};
		}
		return { api, action };
	}

	/**
	 * Load suggestions with customizable display
	 * @param {string} searchText - The text to search for suggestions
	 * @param {number} limit - The maximum number of suggestions to return
	 * @param {number} page - The page number for pagination
	 * @returns {Promise<Object>} - An object containing results and a boolean indicating if more
	 */
	async function loadSuggestions(searchText, limit = 10, page = 1) {
		try {
			const apiName = whitelistDynamic?.apiName;
			const actionName = whitelistDynamic?.action;
			const params = whitelistDynamic?.params || {};

			if (
				lod_.isNil(params) ||
				lod_.isEmpty(params) ||
				lod_.isNil(actionName) ||
				lod_.isNil(apiName)
			) {
				displayError();
				return { results: [], hasMore: false };
			}

			const API = listOfApis.find(item => item.name === apiName);

			if (lod_.isNil(API) || lod_.isEmpty(API)) {
				displayError();
				return { results: [], hasMore: false };
			}

			const action = lod_.get(API, `config.action.${actionName}`, null);
			if (lod_.isNil(action) || lod_.isEmpty(action)) {
				displayError();
				return { results: [], hasMore: false };
			}

			const { searchFields } = params;

			const query = {
				$or: []
			};

			for (let field of searchFields) {
				query.$or.push({
					[field]: { $regex: searchText, $options: "i" }
				});
			}

			// Collection can be overridden by params.collection
			const collection = params.collection ?? action.collection;

			// Merge collections is an array of collections name, that will be requested in the same time
			// The result will be merged in the same array
			const mergeCollections = action.mergeCollections || [];

			const account = action.account ?? false;

			let config = {
				method: action.method ?? "GET",
				maxBodyLength: Infinity,
				url: `${API.config.host}/api/v1/ressource/${collection}/items?search=${searchText}&page=${page}&limit=${limit}&account=${account}`,
				headers: {
					Authorization: `Bearer ${user.token}`,
					assistantID,
					"Content-Type": "application/json"
				}
			};

			if (config.method === "GET") {
				config.data = JSON.stringify({ query });
			} else {
				config.data = {
					filter: action.filter,
					searchFields,
					mergeCollections,
					attribute: whitelistDynamic?.params?.attribute ?? "code", // Optional
					page,
					limit,
					query
				};
			}

			const result = await new Promise((resolve, reject) => {
				axios
					.request(config)
					.then(response => {
						resolve(response.data);
					})
					.catch(error => {
						reject(error);
					});
			});

			const results = result?.results || [];
			const currentPage = result?.currentPage || 1;
			const totalPages = result?.totalPages || 1;

			const hasMore = results.length === limit && currentPage < totalPages; // If we got less than the limit, no more data

			return { results, hasMore };
		} catch (error) {
			displayError();
			return { results: [], hasMore: false };
		}
	}

	/**
	 * Search for suggestions based on the input value
	 * @param {string} val - The search text
	 * @param {number} page - The page number for pagination
	 * @param {boolean} append - Whether to append results to the existing options
	 * @returns {Promise<void>} - A promise that resolves when the search is complete
	 */
	const search = async (val, page = 1, append = false) => {
		setLoading(true);
		try {
			const { results, hasMore: moreAvailable } = await loadSuggestions(val, 10, page);

			if (append) {
				setOptions(prevOptions => [...prevOptions, ...results]);
			} else {
				setOptions(results);
				setCurrentPage(1);
				setCurrentSearchText(val);
			}

			setHasMore(moreAvailable);
			setCurrentPage(page);
		} catch (error) {
			displayError();
		} finally {
			setLoading(false);
		}
	};

	// 1. Memoize the getValue function
	const getValue = useMemo(() => {
		if (isArray) {
			return currentValue || [];
		}
		return currentValue || null;
	}, [currentValue, isArray]);

	/**
	 * Debounced search function to avoid excessive API calls
	 */
	const debouncedSearch = useCallback(
		lod_.debounce(val => {
			search(val);
		}, 300),
		[]
	);

	/**
	 * Load more results when the user scrolls to the bottom of the listbox
	 */
	const loadMore = async () => {
		if (hasMore && !loading) {
			const nextPage = currentPage + 1;
			await search(currentSearchText, nextPage, true);
		}
	};

	// 3. Memoize getCorrectAction
	const { api, action } = useMemo(() => getCorrectAction(), [whitelistDynamic, listOfApis]);

	/**
	 * From a given option, render the label
	 * @param {Object} option - The option to render
	 * @returns {JSX.Element|string} - The rendered label for the option
	 */
	const getOptionLabel = useCallback(
		option => {
			const { action = {} } = getCorrectAction();
			// Display mode can be "text" / "grid"
			const displayMode = whitelistDynamic.params.displayMode || "text";
			// Display, when it's "text" is: " {{{ json code }}} {{{ json name }}}", and when it's "grid" is: ["code", "name"]
			const display = whitelistDynamic.params.display || "code";
			switch (displayMode) {
				case "grid": {
					const displayFields = Array.isArray(display) ? display : [display];

					return (
						<MDBox display="flex" flexDirection="row" alignItems="center">
							{displayFields.map((field, index) => {
								let fieldValue = lod_.get(option, field);

								const cellElement = {
									value: fieldValue,
									column: {
										id: field
									},
									row: {
										original: option,
										values: option
									},
									render: () => {
										return fieldValue;
									}
								};

								return (
									<MDBox key={index} m={0.5}>
										{handleSpecialCell(
											cellElement,
											field,
											dictionary,
											{ collectionDefault: action?.collection },
											null,
											action?.collection
										)}
									</MDBox>
								);
							})}
						</MDBox>
					);
				}
				case "text":
				default: {
					const replacedText = replaceInTextHbr(display, option);
					return replacedText;
				}
			}
		},
		[whitelistDynamic, dictionary, action]
	);

	/**
	 * From a given code, render the value
	 * @param {string} option - The code to render
	 */
	const renderValue = async option => {
		const { action } = getCorrectAction();
		if (!lod_.isNil(action) && !lod_.isEmpty(action) && action.collection) {
			const valueToSave = whitelistDynamic?.params?.attribute || "code";
			const objects = await new Promise(resolve => {
				dispatch(
					FormAction.getItemsFromCollection(
						action.collection,
						{
							query: {
								[valueToSave]: isArray ? { $in: option } : option,
								active: { $in: [true, false, null] }
							}
						},
						res => {
							resolve(res.items);
						}
					)
				);
			});

			if (isArray) {
				setCurrentValue(objects);
			} else {
				setCurrentValue(objects[0]);
			}
		}
	};

	/**
	 * Handle changes in the Autocomplete component
	 * @param {Object} event - The event object from the Autocomplete
	 * @param {Object} newValue - The new value selected by the user
	 * @param {string} reason - The reason for the change (e.g., "selectOption", "clear")
	 * @returns {void} - Handles the change event for the Autocomplete component
	 */
	const onChangeAutocomplete = (event, newValue, reason) => {
		if (reason === "selectOption") {
			const attribute = whitelistDynamic?.params?.attribute || "code";

			if (isArray) {
				// If isArray, we expect newValue to be an array of selected options
				const valueToSave = newValue.map(item => lod_.get(item, attribute, item));
				onChange(valueToSave);
			} else {
				const valueToSave = lod_.get(newValue, attribute, newValue);
				onChange(valueToSave);
			}
		} else if (reason === "clear" && event?.type === "click") {
			// Only clear when user explicitly clicks the clear button
			onChange("");
		}
	};

	/**
	 * From a given code, get the dictionary
	 * @param {string} code - The code to query the dictionary
	 * @returns {Promise<void>} - A promise that resolves when the dictionary is fetched
	 */
	const getDictionary = async code => {
		const dictionaryResult = await queryDictionary(dispatch, code);
		setDictionary(dictionaryResult);
	};

	useEffect(() => {
		if (lod_.isNil(value) || lod_.isEmpty(value)) {
			setCurrentValue(null);
			return;
		}
		renderValue(value);
	}, [value]); // Removed listOfApis if not strictly necessary

	useEffect(() => {
		const { action } = getCorrectAction();
		if (!lod_.isNil(action) && !lod_.isEmpty(action) && action.collection) {
			getDictionary(action.collection);
			search("");
		}
	}, [whitelistDynamic, item, listOfApis]);

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	// Handle scroll detection for infinite scroll
	const handleListboxScroll = event => {
		const listboxNode = event.currentTarget;
		const isNearBottom =
			listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

		if (isNearBottom && hasMore && !loading) {
			loadMore();
		}
	};

	// Create options with loading indicator if needed
	const optionsWithLoading = [...options];
	if (loading && hasMore && options.length > 0) {
		optionsWithLoading.push({ isLoading: true, code: "loading-indicator" });
	}

	/**
	 * Get the value to be used in the Autocomplete component
	 * @returns {string|Array|null} - Returns the current value as an array if isArray
	 */
	// const getValue = () => {
	// 	if (isArray) {
	// 		return currentValue || [];
	// 	} else {
	// 		return currentValue || null;
	// 	}
	// };

	return (
		<Autocomplete
			id="autocomplete-whitelist-item-v2"
			disableCloseOnSelect={isArray}
			options={optionsWithLoading}
			filterOptions={options => options}
			onChange={onChangeAutocomplete}
			// Option label rendering
			getOptionLabel={option => {
				if (option.isLoading) return "";
				return getOptionLabel(option);
			}}
			multiple={isArray} // Allow multiple selection if isArray is true
			loading={loading && options.length === 0} // Only show loading on input when no options yet
			// Set the value based on currentValue state
			value={getValue()}
			// Control the input value
			inputValue={inputValue}
			onInputChange={(event, newInputValue, reason) => {
				if (reason === "input") {
					setInputValue(newInputValue);
					debouncedSearch(newInputValue);
				} else if (reason === "clear") {
					setInputValue("");
				}
			}}
			// Disable the input if disabled prop is true
			renderValue={(option, getItemProps) => {
				if (lod_.isNil(option) || lod_.isEmpty(option)) {
					return null;
				}
				return getOptionLabel(option);
			}}
			// Additional props for the Autocomplete component
			ListboxProps={{
				onScroll: handleListboxScroll,
				style: { maxHeight: "35vh" } // Set a max height to enable scrolling
			}}
			// Render options with loading indicator
			renderOption={(props, option) => {
				if (option.isLoading) {
					return (
						<MenuItem {...props} key="loading-indicator" disabled>
							<MDBox display="flex" justifyContent="center" alignItems="center" width="100%" p={1}>
								<CircularProgress size={20} />
							</MDBox>
						</MenuItem>
					);
				}

				return (
					<MenuItem
						{...props}
						key={option.code}
						selected={(isArray && value?.includes(option.code)) || false}
					>
						{getOptionLabel(option)}
					</MenuItem>
				);
			}}
			// Render tags when multiple selection is enabled
			renderTags={(value, getTagProps) => {
				if (isArray) {
					return value.map((option, index) => {
						const onDelete = () => {
							const attribute = whitelistDynamic?.params?.attribute || "code";
							const newValue = value.filter(
								el => lod_.get(el, attribute) !== lod_.get(option, attribute)
							);
							onChangeAutocomplete({}, newValue, "selectOption");
						};

						const component = getOptionLabel(option);

						return (
							<MDBox
								bgColor="light"
								p={0.5}
								m={0.2}
								borderRadius="md"
								display="flex"
								flexDirection="row"
								alignItems="center"
							>
								{component}
								<IconButton sisze="small" onClick={onDelete}>
									<Icon>close</Icon>
								</IconButton>
							</MDBox>
						);
					});
				}
				return null; // No tags to render if not an array
			}}
			// Render input
			renderInput={params => <MDInput {...params} label={label} />}
		/>
	);
};
export default WhitelistItemV2;
